import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plus, Minus, AlertCircle, Check, Leaf } from 'lucide-react';
import { BOWL_BUILDERS, LOGO_URL } from '../mock';

export default function BowlBuilder({ item, open, onOpenChange, onConfirm }) {
  const builder = item ? BOWL_BUILDERS[item.id] : null;
  const [selections, setSelections] = useState({});
  const [qty, setQty] = useState(1);

  // Reset on item change
  useEffect(() => {
    if (!builder) return;
    const initial = {};
    builder.groups.forEach((g) => {
      initial[g.id] = [];
    });
    setSelections(initial);
    setQty(1);
  }, [builder]);

  // Compute price add-ons from selected options (must be before any early return)
  const addonsTotal = useMemo(() => {
    if (!builder) return 0;
    let sum = 0;
    builder.groups.forEach((g) => {
      const chosen = selections[g.id] || [];
      chosen.forEach((name) => {
        const opt = g.options.find((o) => o.name === name);
        if (opt && typeof opt.price === 'number') sum += opt.price;
      });
    });
    return sum;
  }, [builder, selections]);

  if (!item || !builder) return null;

  const toggleMulti = (groupId, optionName, max) => {
    setSelections((prev) => {
      const current = prev[groupId] || [];
      if (current.includes(optionName)) {
        return { ...prev, [groupId]: current.filter((n) => n !== optionName) };
      }
      if (max && current.length >= max) return prev; // capped
      return { ...prev, [groupId]: [...current, optionName] };
    });
  };

  const setRadio = (groupId, optionName) => {
    setSelections((prev) => ({ ...prev, [groupId]: [optionName] }));
  };

  const validate = () => {
    const errors = [];
    builder.groups.forEach((g) => {
      const count = (selections[g.id] || []).length;
      if (g.min > 0 && count < g.min) {
        errors.push(`${g.name}: select at least ${g.min}`);
      }
    });
    return errors;
  };

  const errors = validate();
  const isValid = errors.length === 0;
  const unitPrice = item.price + addonsTotal;
  const totalPrice = (unitPrice * qty).toFixed(2);

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({ ...item, price: unitPrice, qty, modifiers: selections });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FBF8EF] border border-[#D6CFB8] p-0 sm:max-w-2xl w-[calc(100vw-1rem)] h-auto max-h-[min(92vh,900px)] flex flex-col overflow-hidden gap-0">
        {/* Image header */}
        <div className="relative h-40 sm:h-52 overflow-hidden bg-[#E6EBD5] shrink-0">
          <img src={item.image} alt={item.name} className={item.image === LOGO_URL ? 'w-full h-full object-contain p-8 bg-[#E6EBD5]' : 'w-full h-full object-cover'} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A22]/70 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-xl sm:text-2xl text-[#F6F2E8] leading-tight">
                {builder.title}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Customize your {builder.title}. Pick your grain, vegetables, sauce, protein and packaging.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-[#245A40] text-[#F6F2E8] border-0">
                <Leaf className="w-3 h-3 mr-1" />${item.price.toFixed(2)}
              </Badge>
              {item.calories && (
                <Badge className="bg-[#F6F2E8] text-[#1B2A22] border-0">{item.calories} cal</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable groups — native overflow so the whole ingredient list is always reachable */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-7 py-5">
          <p className="text-sm text-[#3F4A42] leading-relaxed mb-5">{item.description}</p>

          <div className="space-y-5">
            {builder.groups.map((group) => {
              const selected = selections[group.id] || [];
              const isRadio = group.min === 1 && group.max === 1;
              const atMax = group.max && selected.length >= group.max && !isRadio;
              return (
                <div key={group.id}>
                  <GroupHeader group={group} selectedCount={selected.length} />
                  {isRadio ? (
                    <RadioGroup
                      value={selected[0] || ''}
                      onValueChange={(v) => setRadio(group.id, v)}
                      className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-1.5"
                    >
                      {group.options.map((opt) => (
                        <Label
                          key={opt.name}
                          htmlFor={`${group.id}-${opt.name}`}
                          className={`cursor-pointer flex items-start gap-2 rounded-lg border px-2.5 py-2 transition-colors min-h-[52px] ${
                            selected[0] === opt.name
                              ? 'border-[#245A40] bg-[#E6EBD5]'
                              : 'border-[#D6CFB8] bg-[#FFFCF1] hover:border-[#245A40]/60'
                          }`}
                        >
                          <RadioGroupItem
                            value={opt.name}
                            id={`${group.id}-${opt.name}`}
                            className="mt-0.5 border-[#245A40] text-[#245A40] w-3.5 h-3.5"
                          />
                          <OptionLabel opt={opt} />
                        </Label>
                      ))}
                    </RadioGroup>
                  ) : (
                    <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {group.options.map((opt) => {
                        const isSelected = selected.includes(opt.name);
                        const disabled = atMax && !isSelected;
                        return (
                          <label
                            key={opt.name}
                            className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 transition-colors min-h-[52px] ${
                              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            } ${
                              isSelected
                                ? 'border-[#245A40] bg-[#E6EBD5]'
                                : 'border-[#D6CFB8] bg-[#FFFCF1] hover:border-[#245A40]/60'
                            }`}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={disabled}
                              onCheckedChange={() => toggleMulti(group.id, opt.name, group.max)}
                              className="mt-0.5 border-[#245A40] data-[state=checked]:bg-[#245A40] data-[state=checked]:text-[#F6F2E8] w-3.5 h-3.5"
                            />
                            <OptionLabel opt={opt} />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="shrink-0 bg-[#F6F2E8] border-t border-[#D6CFB8] px-5 sm:px-7 py-4 flex sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-[#D6CFB8] rounded-full bg-[#FBF8EF]">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#ECE7D6]"
                aria-label="Decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-sm font-semibold w-7 text-center">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#ECE7D6]"
                aria-label="Increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {!isValid && (
              <div className="hidden sm:flex items-center gap-1 text-xs text-[#A4471A]">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{errors[0]}</span>
              </div>
            )}
          </div>
          <Button
            onClick={handleConfirm}
            disabled={!isValid}
            className="btn-primary rounded-full h-11 px-6 font-semibold w-full sm:w-auto disabled:opacity-50"
          >
            <Check className="w-4 h-4 mr-1.5" /> Add to order · ${totalPrice}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatGroupRule({ min, max }) {
  if (min === 1 && max === 1) return 'Required · choose 1';
  if (min > 0) {
    const range = max && max !== min ? `–${max}` : '';
    return `Required · choose ${min}${range}`;
  }
  if (max) return `Optional · up to ${max}`;
  return 'Optional';
}

function GroupHeader({ group, selectedCount }) {
  const rule = formatGroupRule(group);
  return (
    <div className="flex items-end justify-between">
      <div>
        <div className="font-display text-lg text-[#1B2A22]">{group.name}</div>
        <div className="text-[11px] uppercase tracking-wider text-[#6F6A55] font-semibold mt-0.5">
          {rule}
        </div>
      </div>
      {group.max > 1 && (
        <div className="text-xs text-[#6F6A55]">
          {selectedCount}/{group.max} selected
        </div>
      )}
    </div>
  );
}

function OptionLabel({ opt }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold text-[#1B2A22] leading-tight flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
        <span>{opt.name}</span>
        {opt.included && (
          <span className="text-[9px] uppercase tracking-wider font-bold text-[#245A40] bg-[#E6EBD5] px-1 py-0.5 rounded-full border border-[#CFDAB1]">
            Incl.
          </span>
        )}
        {typeof opt.price === 'number' && opt.price > 0 && (
          <span className="text-[11px] font-bold text-[#E0703B]">+${opt.price.toFixed(2)}</span>
        )}
      </div>
      {opt.diet && (
        <div className="text-[9px] uppercase tracking-wider font-bold text-[#245A40] mt-0.5">
          {opt.diet}
        </div>
      )}
    </div>
  );
}
